// game/pave.js — THE ONE PAVED-SURFACE RULE (WAYSG2 CONTRACT P2). PURE module
// (no three, no fs): the WebXR client (esbuild import: decode.js, borecut.js,
// terrain.js), the node gates (scripts/check-pave.mjs) and the WORLD stage
// (world/corridor.js calls the carriageway section math here) share it.
//
// 1. THE CARRIAGEWAY SECTION (moved verbatim from world/corridor.js
//    sectionTables / secDz / secDzI / prismDz / laneSurface and the segment
//    claim's foot): per station the crown, the carriageway bound and the kerb
//    raise, per run the ballast prism. A station table S is the corridor's
//    (world/corridor.js stationTables) or, built from ways.json alone,
//    `stationsOf(doc)` — the same typed arrays, the same arithmetic in the
//    same order, so both give the SAME float64 heights (check-pave row
//    "pave = corridor").
//      height at offset o of station g:    laneSurface(S, r, g, o)
//      the segment claim (the conform's kind-0 cell height):
//        segFoot(S, g, x, z, out) -> out = [t, o]; laneHi(S, g, t, o)
//      the ROAD LEVEL (no kerb raise, no prism; a walk's ky):
//        carriageway(S, g, t, o)
// 2. THE PAVE INDEX: `paveIndex(doc, drawn, opts)` over the paved drawn
//    pieces (drawnRibbons groundWays; e.paved, never a flight) and the
//    sidewalk areas `doc.walks` (WAYSG2 P1, the WORLD stage's; opts.walks
//    overrides for fixtures): convex plan CELLS (a road piece's section quads
//    split at the crown line; a walk ring cut into convex parts) with a kind,
//    and `paveAt(idx, x, z)` -> { y, kind: 'road' | 'walk' | null, run, edge }.
//      road: the min over the paved road cells holding (x, z) of their run's
//            segment claim at road level (carriageway), the conform's "lowest
//            hi wins" between claims;
//      walk: (before any road) inside a walk cell: ky at the nearest point
//            of its item's kerb polyline (linear between kerb vertices) +
//            walks.h_m — flat across, the road's grade along.
//      platform: (before any walk / road; only a flight's lane wins) inside
//            a platforms item (ways.json `platforms`, the WORLD stage's: a
//            railway platform or the ground under a station canopy) its
//            plane a x + b z + c exactly — flat, paved, never yielded.
//    The bake's ground tree re-meshes the terrain on exactly these cells
//    (client/src/borecut.js cutTile with pave cells, groundm/bakectx.mjs):
//    the drawn ground on a paved way IS paveAt.
'use strict';

const WD = require('./waysdraw.js');
const { perps } = require('./solids.js');

// STEP GENERATORS (client-load-unresponsive): the index's long passes are
// generators that `yield` every few units of work, so the client can spread
// them over many main-thread tasks (client/src/decode.js); drain() runs one
// to the end in one go — the synchronous API every other caller keeps. The
// steps never change what is computed, only where it may pause.
//   STEP_RUNS / STEP_CELLS: masks (2^k - 1) — a yield every 2^k runs / cells;
//   STEP_PIECES: a yield every this many road pieces
const STEP_RUNS = 255, STEP_CELLS = 255, STEP_PIECES = 8;
function drain(it) {
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}

// ------------------------------------------------------- section parameters
// the ballast slope 'V:H' -> V / H (world/corridor.js slopeOf)
function slopeOf(str, what) {
  const m = /^\s*(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)/.exec(String(str));
  if (!m || !(+m[1] > 0) || !(+m[2] > 0)) throw new Error(`pave: ${what} slope "${str}" is not V:H`);
  return +m[1] / +m[2];
}
// the section's parameters from params.json corridor.section (the WORLD
// stage) — world/corridor.js sectionParams' crown / kerb / ballast part
function sectionParamsOf(X) {
  if (!X || !(X.crown >= 0) || !Array.isArray(X.crown_classes) || !(X.kerb_h_m >= 0) || !X.ballast || !Array.isArray(X.ballast.classes)
    || !(X.ballast.top_w_m > 0) || !(X.ballast.prism_h_m >= 0)) {
    throw new Error('pave: corridor.section {crown, crown_classes, kerb_h_m, ballast {classes, top_w_m, prism_h_m, slope}} missing');
  }
  return { crown: X.crown, crownCls: new Set(X.crown_classes), kerb: X.kerb_h_m, balCls: new Set(X.ballast.classes),
    bh: X.ballast.top_w_m / 2, ph: X.ballast.prism_h_m, bs: slopeOf(X.ballast.slope, 'ballast prism') };
}
// the same from a ways.json (the published doc.crown / doc.kerb / doc.ballast)
function sectionParamsDoc(doc) {
  const C = doc.crown, K = doc.kerb, B = doc.ballast;
  if (!C || !K || !B) throw new Error('pave: ways.json carries no crown / kerb / ballast (a world before WAYS-G W-e)');
  return sectionParamsOf({ crown: C.fall, crown_classes: C.classes, kerb_h_m: K.h_m,
    ballast: { classes: B.classes, top_w_m: B.top_w_m, prism_h_m: B.prism_h_m, slope: B.slope } });
}
// a run's crown eligibility: a two-way road with a drawn centre line
// (game/waysdraw.js laneLayout: b and f lanes both > 0)
function crowned(e, rings, X) {
  if (!X.crownCls.has(e.pcls) || !(X.crown > 0)) return false;
  const L = WD.laneLayout(e, rings, Infinity);
  return L.b > 0 && L.f > 0;
}
// THE SECTION TABLES of station table S (world/corridor.js sectionTables):
// per station the carriageway bound eL / eR (the sidewalk strips e.sw inside
// the formation, never past the other side), its centre oc, the kerb raise
// kL / kR where a sidewalk is mapped, the crown cr less the cross-fall; per
// run the ballast flag and the crown flag
function sectionTables(doc, S, X) { drain(sectionSteps(doc, S, X)); }
function* sectionSteps(doc, S, X) {
  const N = S.N, runs = doc.ways;
  const pd = doc.corridor && doc.corridor.rules ? doc : { ...doc, corridor: { ...(doc.corridor || {}), rules: S.rules } };
  const rings = Array.isArray(doc.rings) ? WD.paintDoc(pd, Infinity) : null;
  S.X = X;
  S.cr = new Float32Array(N); S.oc = new Float32Array(N); S.eL = new Float32Array(N); S.eR = new Float32Array(N);
  S.kL = new Float32Array(N); S.kR = new Float32Array(N);
  S.bal = new Uint8Array(runs.length); S.crowned = new Uint8Array(runs.length);
  for (let r = 0; r < runs.length; r++) {
    if ((r & STEP_RUNS) === STEP_RUNS) yield;
    const e = runs[r], h = e.w / 2, cr = crowned(e, rings, X);
    S.crowned[r] = cr ? 1 : 0;
    S.bal[r] = X.balCls.has(e.pcls) ? 1 : 0;
    for (let i = 0; i < e.x.length; i++) {
      const g = S.off[r] + i;
      let l = e.sw ? e.sw[i][0] : 0, rr = e.sw ? e.sw[i][1] : 0;
      // (the paintParts bound: never past the other side)
      if (l + rr > e.w) { const f = e.w / (l + rr); l *= f; rr *= f; }
      S.eL[g] = -h + l; S.eR[g] = h - rr; S.oc[g] = 0.5 * (S.eL[g] + S.eR[g]);
      S.kL[g] = l > 0 ? X.kerb : 0; S.kR[g] = rr > 0 ? X.kerb : 0;
      S.cr[g] = cr ? Math.max(0, X.crown - Math.abs(S.cf[g])) : 0;
    }
  }
}
// the lane surface over the lane plane at offset o of station g (crown,
// kerb raise; the ballast prism is prismDz, per run). Past a carriageway
// edge with a kerb the sidewalk holds the edge's height + the kerb; past
// one without, the crown's fall runs on over the shoulder (no sag at the
// lane edge)
function secDz(S, g, o) {
  const a = o < S.eL[g] ? (S.kL[g] > 0 ? S.eL[g] : o) : (o > S.eR[g] ? (S.kR[g] > 0 ? S.eR[g] : o) : o);
  let dz = -S.cr[g] * Math.abs(a - S.oc[g]);
  if (o < S.eL[g]) dz += S.kL[g]; else if (o > S.eR[g]) dz += S.kR[g];
  return dz;
}
// the same between stations g and g + 1 at fraction t: the section's
// parameters interpolated (a segment's cells carry no step at mid-segment)
function secDzI(S, g, t, o) {
  if (!(t > 0)) return secDz(S, g, o);
  const u = 1 - t, h = g + 1;
  const eL = u * S.eL[g] + t * S.eL[h], eR = u * S.eR[g] + t * S.eR[h], oc = u * S.oc[g] + t * S.oc[h], cr = u * S.cr[g] + t * S.cr[h];
  const kL = u * S.kL[g] + t * S.kL[h], kR = u * S.kR[g] + t * S.kR[h];
  const a = o < eL ? (kL > 0 ? eL : o) : (o > eR ? (kR > 0 ? eR : o) : o);
  let dz = -cr * Math.abs(a - oc);
  if (o < eL) dz += kL; else if (o > eR) dz += kR;
  return dz;
}
// the crown term alone (secDzI without the kerb raise): the ROAD LEVEL's
// section. Past a kerbed edge the edge's height is held (the walk's ky);
// past an unkerbed one the crown's fall runs on.
function crownDzI(S, g, t, o) {
  let eL, eR, oc, cr, kL, kR;
  if (!(t > 0)) { eL = S.eL[g]; eR = S.eR[g]; oc = S.oc[g]; cr = S.cr[g]; kL = S.kL[g]; kR = S.kR[g]; } else {
    const u = 1 - t, h = g + 1;
    eL = u * S.eL[g] + t * S.eL[h]; eR = u * S.eR[g] + t * S.eR[h]; oc = u * S.oc[g] + t * S.oc[h]; cr = u * S.cr[g] + t * S.cr[h];
    kL = u * S.kL[g] + t * S.kL[h]; kR = u * S.kR[g] + t * S.kR[h];
  }
  const a = o < eL ? (kL > 0 ? eL : o) : (o > eR ? (kR > 0 ? eR : o) : o);
  return -cr * Math.abs(a - oc);
}
// the ballast prism at |o| = ao of run r (0 inside the bed, the formation
// prism_h_m under it past the prism's foot)
function prismDz(S, r, ao) {
  if (!S.bal[r] || !(ao > S.X.bh)) return 0;
  const d = (ao - S.X.bh) * S.X.bs;
  return -(d < S.X.ph ? d : S.X.ph);
}
// the lane surface (the ground of a surface station) at offset o of
// station g of run r: y + cf o + crown / kerb + prism
function laneSurface(S, r, g, o) {
  return S.y[g] + S.cf[g] * o + secDz(S, g, o) + prismDz(S, r, Math.abs(o));
}
// THE SEGMENT CLAIM'S FOOT (world/corridor.js conform, claim kind 0): the
// point (x, z) on segment g -> g + 1 at fraction t whose interpolated station
// normal passes through (x, z), o the offset along it (the quadratic in t; of
// two roots in [0, 1] the one nearer the centre line). false: no foot.
function segFoot(S, g, x, z, out) {
  const Ax = S.x[g], Az = S.z[g], Dx = S.x[g + 1] - Ax, Dz = S.z[g + 1] - Az;
  const Nax = S.nx[g], Naz = S.nz[g], Mx = S.nx[g + 1] - Nax, Mz = S.nz[g + 1] - Naz;
  const c2 = -(Dx * Mz - Dz * Mx), cDN = Dx * Naz - Dz * Nax;
  const rx = x - Ax, rz = z - Az;
  const c0 = rx * Naz - rz * Nax, c1 = (rx * Mz - rz * Mx) - cDN;
  let f = NaN, f2 = NaN;
  if (Math.abs(c2) < 1e-9) { if (Math.abs(c1) > 1e-12) f = -c0 / c1; }
  else {
    const disc = c1 * c1 - 4 * c2 * c0;
    if (disc < 0) return false;
    const q = -0.5 * (c1 + (c1 >= 0 ? 1 : -1) * Math.sqrt(disc));
    f = q / c2; f2 = q !== 0 ? c0 / q : NaN;
  }
  let best = NaN, bo = Infinity;
  for (let rt = 0; rt < 2; rt++) {
    const ff = rt ? f2 : f;
    if (!(ff >= 0 && ff <= 1)) continue;
    const Nx = Nax + ff * Mx, Nz = Naz + ff * Mz, nn = Nx * Nx + Nz * Nz;
    const oq = ((rx - ff * Dx) * Nx + (rz - ff * Dz) * Nz) / nn;
    if (Math.abs(oq) < Math.abs(bo)) { bo = oq; best = ff; }
  }
  if (!(best >= 0)) return false;
  out[0] = best; out[1] = bo;
  return true;
}
// the segment claim's height at (t, o) on segment g (the conform's formation
// hi, kerb raise included): y + cf o + secDzI
function laneHi(S, g, t, o) {
  const ya = S.y[g], yb = S.y[g + 1], cfa = S.cf[g], cfb = S.cf[g + 1];
  const y = ya + t * (yb - ya), cf = cfa + t * (cfb - cfa);
  return y + cf * o + secDzI(S, g, t, o);
}
// THE ROAD LEVEL at (t, o) on segment g: the claim's height without the
// kerb raise (and without a prism: no paved run is ballast) — the
// carriageway surface; a walk's ky (WAYSG2 P1) is this at its kerb line
function carriageway(S, g, t, o) {
  const ya = S.y[g], yb = S.y[g + 1], cfa = S.cf[g], cfb = S.cf[g + 1];
  const y = ya + t * (yb - ya), cf = cfa + t * (cfb - cfa);
  return y + cf * o + crownDzI(S, g, t, o);
}

// ------------------------------------------------------- the station table
// world/corridor.js stationTables' geometry from ways.json alone: per station
// x, z, the normal (game/solids.js perps on the run's own polyline), y (the
// lane: e.y, a flight's lane by waysdraw flightLaneOf, less the lift), cf;
// then the section tables. (An open track's clearance is not applied: no
// paved run is one.)
function stationsOf(doc) { return drain(stationSteps(doc)); }
function* stationSteps(doc) {
  const runs = doc.ways;
  const off = new Int32Array(runs.length + 1);
  for (let r = 0; r < runs.length; r++) off[r + 1] = off[r] + runs[r].x.length;
  const N = off[runs.length];
  const S = { off, N, x: new Float64Array(N), z: new Float64Array(N), nx: new Float64Array(N), nz: new Float64Array(N),
    y: new Float64Array(N), cf: new Float32Array(N), flag: new Uint8Array(N), rH: new Float32Array(runs.length),
    rules: doc.corridor && doc.corridor.rules };
  const plane = WD.flightPlane(doc);
  for (let r = 0; r < runs.length; r++) {
    if ((r & STEP_RUNS) === STEP_RUNS) yield;
    const e = runs[r];
    S.rH[r] = e.w / 2;
    const pp = perps(e.x.map((x, i) => [x, 0, e.z[i]]));
    const base = WD.isFlightRun(doc, e) ? WD.flightLaneOf(doc, e, plane) : e.y;
    for (let i = 0; i < e.x.length; i++) {
      const g = off[r] + i;
      S.x[g] = e.x[i]; S.z[g] = e.z[i];
      S.nx[g] = pp[i][0]; S.nz[g] = pp[i][1];
      S.y[g] = base[i] - e.lift;
      S.cf[g] = e.cf[i];
      S.flag[g] = e.flag[i];
    }
  }
  applyRamps(S, doc.walks);
  yield* sectionSteps(doc, S, sectionParamsDoc(doc));
  return S;
}
// (sep-ramp) THE FOOT RUN'S OWN RIBBON MEETS ITS STRIP: the published
// walks.ramps [[foot run, station, dy]] (game/walks.js pieceRamps) added to
// the stations' y — the one implementation world/corridor.js walksOn and
// stationSteps share (check-pave 'pave = corridor')
function applyRamps(S, walks) {
  const R = walks && walks.ramps;
  if (!Array.isArray(R)) return;
  for (const [f, i, dy] of R) S.y[S.off[f] + i] += dy;
}

// ------------------------------------------------------- plan geometry
// signed area of a flat [x, z, x, z, ...] ring (> 0: CCW with x the first
// axis and z the second)
function ringArea(P) {
  let a = 0;
  const n = P.length / 2;
  for (let i = 0; i < n; i++) { const j = (i + 1) % n; a += P[2 * i] * P[2 * j + 1] - P[2 * j] * P[2 * i + 1]; }
  return a / 2;
}
// a CONVEX CCW polygon -> the half-spaces of the clipper (client/src/
// borecut.js volume format: f = A x + B z + C y + D <= 0 inside, unit plan
// normal, C 0), its plan box; null when degenerate
const MIN_EDGE = 1e-6;
function cellOf(P, tag) {
  const n = P.length / 2;
  const H = [], SG = [];
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const px = P[2 * i], pz = P[2 * i + 1], ux = P[2 * j] - px, uz = P[2 * j + 1] - pz, L = Math.hypot(ux, uz);
    if (px < x0) x0 = px; if (px > x1) x1 = px; if (pz < z0) z0 = pz; if (pz > z1) z1 = pz;
    if (!(L > MIN_EDGE)) continue;
    H.push(uz / L, -ux / L, 0, (ux * pz - uz * px) / L);
    SG.push(px, pz, P[2 * j], P[2 * j + 1]);
  }
  if (H.length < 12) return null;
  // seg: per half-space its polygon side (x0, z0, x1, z1); gid: its shared
  // line id (paveIndex shareLines). (new TypedArray(array) = TypedArray.from
  // without the iterator protocol: the same values, ~4x cheaper per cell)
  const nk = H.length / 4, vk = new Int32Array(nk);
  for (let k = 0; k < nk; k++) vk[k] = k;
  return { P: new Float64Array(H), nk, vk,
    x0, x1, z0, z1, y0: -Infinity, y1: Infinity, poly: new Float64Array(P), seg: new Float64Array(SG), gid: null, clear: 0, ...tag };
}
// convex and CCW (every turn left or straight), area > eps
function convexCCW(P, eps = 1e-9) {
  const n = P.length / 2;
  if (!(ringArea(P) > eps)) return false;
  for (let i = 0; i < n; i++) {
    const a = (i + n - 1) % n, b = i, c = (i + 1) % n;
    const cr = (P[2 * b] - P[2 * a]) * (P[2 * c + 1] - P[2 * a + 1]) - (P[2 * b + 1] - P[2 * a + 1]) * (P[2 * c] - P[2 * a]);
    if (cr < -eps) return false;
  }
  return true;
}
// signed plan distance of (x, z) inside a cell (> 0 inside; the smallest
// margin over its half-spaces)
function cellDepth(c, x, z) {
  const P = c.P;
  let d = Infinity;
  for (let k = 0; k < c.nk; k++) {
    const f = P[4 * k] * x + P[4 * k + 1] * z + P[4 * k + 3];
    if (-f < d) d = -f;
  }
  return d;
}

// a simple polygon (flat CCW ring) -> convex parts (ear clipping, then a
// greedy merge across diagonals while the union stays convex:
// Hertel-Mehlhorn) as flat rings
function convexParts(P) {
  const n = P.length / 2;
  if (n < 3) return [];
  const X = (i) => P[2 * i], Z = (i) => P[2 * i + 1];
  const cross = (a, b, c) => (X(b) - X(a)) * (Z(c) - Z(a)) - (Z(b) - Z(a)) * (X(c) - X(a));
  // ear clipping on a linked ring, the ear with the SHORTEST diagonal first
  // (a strip is cut straight across, never fanned into long thin wedges
  // whose near-parallel sides the clipper meets at grazing angles); an ear's
  // containment test reads only the reflex vertices (the only ones that can
  // lie inside it)
  const prev = new Int32Array(n), next = new Int32Array(n), gone = new Uint8Array(n), ver = new Int32Array(n);
  for (let i = 0; i < n; i++) { prev[i] = (i + n - 1) % n; next[i] = (i + 1) % n; }
  const reflex = new Set();
  const turn = (i) => cross(prev[i], i, next[i]);
  for (let i = 0; i < n; i++) if (!(turn(i) > 1e-12)) reflex.add(i);
  const isEar = (b) => {
    if (reflex.has(b)) return false;
    const a = prev[b], c = next[b];
    for (const q of reflex) {
      if (q === a || q === c) continue;
      if (cross(a, b, q) >= 0 && cross(b, c, q) >= 0 && cross(c, a, q) >= 0) return false;
    }
    return true;
  };
  // a binary heap of [diagonal^2, vertex, version]
  const H = [];
  const less = (i, j) => H[i][0] < H[j][0];
  const push = (e) => {
    H.push(e);
    for (let i = H.length - 1; i > 0;) { const p = (i - 1) >> 1; if (!less(i, p)) break; [H[i], H[p]] = [H[p], H[i]]; i = p; }
  };
  const pop = () => {
    const top = H[0], last = H.pop();
    if (H.length) {
      H[0] = last;
      for (let i = 0; ;) {
        const l = 2 * i + 1, r = l + 1;
        let s = i;
        if (l < H.length && less(l, s)) s = l;
        if (r < H.length && less(r, s)) s = r;
        if (s === i) break;
        [H[i], H[s]] = [H[s], H[i]]; i = s;
      }
    }
    return top;
  };
  const key = (b) => { const a = prev[b], c = next[b]; return (X(c) - X(a)) ** 2 + (Z(c) - Z(a)) ** 2; };
  const tris = [];
  let m = n;
  const clip = (b, keep) => {
    const a = prev[b], c = next[b];
    if (keep) tris.push([a, b, c]);
    next[a] = c; prev[c] = a; reflex.delete(b); gone[b] = 1; m--;
    for (const v of [a, c]) {
      if (turn(v) > 1e-12) reflex.delete(v); else reflex.add(v);
      ver[v]++; push([key(v), v, ver[v]]);
    }
  };
  for (let i = 0; i < n; i++) push([key(i), i, 0]);
  let stall = 0;
  while (m > 3) {
    if (!H.length) {
      // every queued vertex was blocked (a reflex vertex inside it, since
      // removed, or none at all): queue the rest again, once; then the
      // fallback — a flat vertex dropped (no corner), else a convex one
      // clipped regardless (collinear runs / a slightly self-touching ring)
      let any = -1;
      for (let i = 0; i < n; i++) if (!gone[i]) { if (any < 0) any = i; push([key(i), i, ver[i]]); }
      if (stall++ > 0) {
        let b = -1;
        for (let i = 0; i < n && b < 0; i++) if (!gone[i] && Math.abs(turn(i)) <= 1e-12) b = i;
        if (b >= 0) { clip(b, false); stall = 0; continue; }
        for (let i = 0; i < n && b < 0; i++) if (!gone[i] && turn(i) > 0) b = i;
        clip(b >= 0 ? b : any, true); stall = 0;
        H.length = 0;
        for (let i = 0; i < n; i++) if (!gone[i]) push([key(i), i, ver[i]]);
      }
      continue;
    }
    const [, b, v] = pop();
    if (gone[b] || v !== ver[b] || !isEar(b)) continue;
    clip(b, true); stall = 0;
  }
  if (m === 3) { let b = 0; while (gone[b]) b++; if (cross(prev[b], b, next[b]) > 1e-12) tris.push([prev[b], b, next[b]]); }
  // Hertel-Mehlhorn: drop a diagonal (u, w) shared by two parts whenever
  // their union stays convex; the directed edge map tracks each edge's part
  const parts = tris.map((t) => t.slice()), alive = parts.map(() => true);
  const edge = new Map();
  const ek = (u, w) => u * 1048576 + w;
  parts.forEach((L, pi) => { for (let k = 0; k < L.length; k++) edge.set(ek(L[k], L[(k + 1) % L.length]), pi); });
  const isConvex = (L) => {
    for (let k = 0; k < L.length; k++) if (cross(L[(k + L.length - 1) % L.length], L[k], L[(k + 1) % L.length]) < -1e-12) return false;
    return true;
  };
  for (let pi = 0; pi < parts.length; pi++) {
    if (!alive[pi]) continue;
    for (let again = true; again;) {
      again = false;
      const L = parts[pi];
      for (let k = 0; k < L.length; k++) {
        const u = L[k], w = L[(k + 1) % L.length], mi = edge.get(ek(w, u));
        if (mi === undefined || mi === pi || !alive[mi]) continue;
        const M = parts[mi], mk = M.indexOf(w);
        // L ... u w ...; M ... w u ...: [w ... u] of L, then M after u up to before w
        const out = [];
        for (let q = 0; q < L.length; q++) out.push(L[(k + 1 + q) % L.length]);
        for (let q = 2; q < M.length; q++) out.push(M[(mk + q) % M.length]);
        if (!isConvex(out)) continue;
        edge.delete(ek(u, w)); edge.delete(ek(w, u));
        for (let q = 0; q < M.length; q++) { const key = ek(M[q], M[(q + 1) % M.length]); if (edge.get(key) === mi) edge.set(key, pi); }
        parts[pi] = out; alive[mi] = false; again = true;
        break;
      }
    }
  }
  return parts.filter((_, i) => alive[i]).map((L) => { const R = []; for (const i of L) R.push(X(i), Z(i)); return R; });
}

// ------------------------------------------------------- the pave index
// the paved predicate (waysdraw / the corridor paint: e.paved), never a
// flight (its ground is its plane and its treads) nor rail (ballast keeps
// its shape)
function pavedRun(doc, e) {
  return !!(e && e.paved) && !WD.isFlightRun(doc, e) && e.cls !== 'rail' && e.cls !== 'funicular';
}
const GRID_M = 8;
const KIND = { road: 1, walk: 2, flight: 3, platform: 4 };

// ------------------------------------------------------- the walks (P1)
// THE WALK TOP RULE (CONTRACT P1), the one evaluator of doc.walks heights
// (WAYSG2-K's corridor raise and paveAt both call it): inside a walks item
// the top is ky at the nearest point of its kerb polyline (linear between
// kerb vertices) + walks.h_m — flat across, the road's grade along.
// walksIndex(src): src = a ways.json doc (reads doc.walks) or a walks block
// { rev, h_m, items: [{ id, side_of, ring: [[x, z]...] CCW, kerb: [[i0, i1]
// ...] inclusive ring spans (may wrap), ky: [y per ring vertex] }] }; null
// when the doc has none. Items that break the schema are counted (bad), not
// indexed. -> { h_m, items, grid, bad }
function walksIndex(src) {
  const W = src && Array.isArray(src.ways) ? (src.walks || null) : src;
  if (!W) return null;
  if (!(W.h_m >= 0) || !Array.isArray(W.items)) throw new Error('pave: walks {rev, h_m, items} malformed');
  const items = [];
  let bad = 0;
  W.items.forEach((it, k) => {
    const ring = it.ring, n = ring ? ring.length : 0;
    if (n < 3 || !Array.isArray(it.ky) || it.ky.length !== n) { bad++; return; }
    const flat = [];
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
    for (const p of ring) {
      flat.push(p[0], p[1]);
      if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0]; if (p[1] < z0) z0 = p[1]; if (p[1] > z1) z1 = p[1];
    }
    if (!(ringArea(flat) > 0)) { bad++; return; }
    // the kerb polyline: every span [i0, i1] (inclusive, may wrap) as
    // segments [x0, z0, y0, x1, z1, y1]
    const seg = [], kpairs = [];
    for (const sp of it.kerb || []) {
      let i = sp[0];
      for (let guard = 0; guard < n && i !== sp[1]; guard++) {
        const j = (i + 1) % n;
        seg.push(ring[i][0], ring[i][1], it.ky[i], ring[j][0], ring[j][1], it.ky[j]);
        kpairs.push([i, j]);
        i = j;
      }
    }
    items.push({ id: it.id != null ? it.id : k, k, side_of: it.side_of || [], ring: flat, ky: it.ky, kseg: Float64Array.from(seg), kpairs, x0, x1, z0, z1 });
  });
  const grid = new Map();
  items.forEach((it, i) => {
    for (let gz = Math.floor(it.z0 / GRID_M); gz <= Math.floor(it.z1 / GRID_M); gz++) {
      for (let gx = Math.floor(it.x0 / GRID_M); gx <= Math.floor(it.x1 / GRID_M); gx++) {
        const key = gx + ':' + gz;
        let a = grid.get(key);
        if (!a) grid.set(key, a = []);
        a.push(i);
      }
    }
  });
  return { h_m: W.h_m, items, grid, bad };
}
// the walk top of item it at (x, z) (inside or not): { y, d } — d the plan
// distance to its kerb polyline (no kerb span: the nearest ring vertex's ky)
function walkTopOf(it, h, x, z) {
  const K = it.kseg;
  let bd = Infinity, by = NaN;
  for (let s = 0; s < K.length; s += 6) {
    const ax = K[s], az = K[s + 1], dx = K[s + 3] - ax, dz = K[s + 4] - az, l2 = dx * dx + dz * dz;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / l2)) : 0;
    const d = Math.hypot(x - ax - t * dx, z - az - t * dz);
    if (d < bd) { bd = d; by = K[s + 2] + t * (K[s + 5] - K[s + 2]); }
  }
  if (!(bd < Infinity)) {
    const R = it.ring;
    for (let i = 0; i < R.length / 2; i++) { const d = Math.hypot(x - R[2 * i], z - R[2 * i + 1]); if (d < bd) { bd = d; by = it.ky[i]; } }
  }
  return { y: by + h, d: bd };
}
// (x, z) in a flat ring (even-odd; a point on the boundary counts within
// tol m)
function inRing(R, x, z, tol = 0) {
  const n = R.length / 2;
  let c = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = R[2 * i], zi = R[2 * i + 1], xj = R[2 * j], zj = R[2 * j + 1];
    if ((zi > z) !== (zj > z) && x < (xj - xi) * (z - zi) / (zj - zi) + xi) c = !c;
  }
  if (c || !(tol > 0)) return c;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const ax = R[2 * j], az = R[2 * j + 1], dx = R[2 * i] - ax, dz = R[2 * i + 1] - az, l2 = dx * dx + dz * dz;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / l2)) : 0;
    if (Math.hypot(x - ax - t * dx, z - az - t * dz) <= tol) return true;
  }
  return false;
}
// THE WALK TOP at (x, z): y (the highest item holding it, its boundary
// within tol m) or null outside every walk. walkTopAt(widx, x, z, tol, out):
// out (optional) receives { item, d }.
function walkTopAt(widx, x, z, tol = 0, out = null) {
  if (!widx) return null;
  const a = widx.grid.get(Math.floor(x / GRID_M) + ':' + Math.floor(z / GRID_M));
  if (!a) return null;
  let best = null;
  for (const i of a) {
    const it = widx.items[i];
    if (x < it.x0 - tol || x > it.x1 + tol || z < it.z0 - tol || z > it.z1 + tol || !inRing(it.ring, x, z, tol)) continue;
    const v = walkTopOf(it, widx.h_m, x, z);
    if (best === null || v.y > best) { best = v.y; if (out) { out.item = i; out.d = v.d; } }
  }
  return best;
}

// ------------------------------------------------------- the platforms
// THE PLATFORM RULE (ways.json `platforms`, the WORLD stage's): a railway
// platform, or the ground under a station canopy, is a FLAT PAVED area of
// the one ground — its ring (final, clipped, CCW, world m) at its plane
// y = a x + b z + c; per ring side i -> i + 1 `edge` true = track-facing: a
// vertical face drops from the plane to the bed beside it (the pave cut's
// wall, client/src/borecut.js). The WORLD stage writes the plane into L0 at
// every grid node inside the ring, so the grid already stands on it there.
// platformsIndex(src): src = a ways.json doc (reads doc.platforms) or a
// platforms block { rev, items: [{ id, group, kind 'platform' | 'canopy',
// src, ring: [[x, z]...], plane: [a, b, c], mat, edge: [bool per side] }] };
// null when there is none. Items that break the schema are counted (bad),
// not indexed. -> { items, grid, bad }
const PLATFORMS_REV = 1;
const VERTEX_TOL_M = 1e-3;
function platformsIndex(src) {
  const B = src && Array.isArray(src.ways) ? (src.platforms || null) : src;
  if (!B) return null;
  if (B.rev !== PLATFORMS_REV || !Array.isArray(B.items)) throw new Error(`pave: platforms rev ${B.rev}: this reader knows rev ${PLATFORMS_REV} {rev, items}`);
  const items = [];
  let bad = 0;
  B.items.forEach((it, k) => {
    const ring = it.ring, n = ring ? ring.length : 0, pl = it.plane;
    if (n < 3 || !Array.isArray(pl) || pl.length !== 3 || !pl.every(Number.isFinite) || !Array.isArray(it.edge) || it.edge.length !== n) { bad++; return; }
    const flat = [];
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
    for (const p of ring) {
      flat.push(p[0], p[1]);
      if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0]; if (p[1] < z0) z0 = p[1]; if (p[1] > z1) z1 = p[1];
    }
    if (!(ringArea(flat) > 0) || !flat.every(Number.isFinite)) { bad++; return; }
    items.push({ id: it.id != null ? it.id : k, k, group: it.group != null ? it.group : null, kind: it.kind === 'canopy' ? 'canopy' : 'platform',
      mat: typeof it.mat === 'string' ? it.mat : null, ring: flat, plane: [pl[0], pl[1], pl[2]], edge: Uint8Array.from(it.edge, (e) => (e ? 1 : 0)), x0, x1, z0, z1 });
  });
  // the box grid, each box grown by PLAT_PAD_M (a grid cell's node inside
  // a ring is found from any point of the cell: platformGround), numeric
  // keys (the heightfields sample it per height(): no key string)
  const grid = new Map();
  items.forEach((it, i) => {
    for (let gz = Math.floor((it.z0 - PLAT_PAD_M) / GRID_M); gz <= Math.floor((it.z1 + PLAT_PAD_M) / GRID_M); gz++) {
      for (let gx = Math.floor((it.x0 - PLAT_PAD_M) / GRID_M); gx <= Math.floor((it.x1 + PLAT_PAD_M) / GRID_M); gx++) {
        const key = platKey(gx, gz);
        let a = grid.get(key);
        if (!a) grid.set(key, a = []);
        a.push(i);
      }
    }
  });
  return { items, grid, bad };
}
const PLAT_PAD_M = 1;
const platKey = (gx, gz) => (gx + 32768) * 65536 + (gz + 32768);
const platCands = (pidx, x, z) => pidx.grid.get(platKey(Math.floor(x / GRID_M), Math.floor(z / GRID_M)));
// the plane of item it at (x, z)
function planeY(it, x, z) { return it.plane[0] * x + it.plane[1] * z + it.plane[2]; }
// the platforms item holding (x, z) (its boundary within tol m; overlapping
// items: the highest plane), or -1
function platformAt(pidx, x, z, tol = 0) {
  if (!pidx) return -1;
  const a = platCands(pidx, x, z);
  if (!a) return -1;
  let best = -1, by = -Infinity;
  for (const i of a) {
    const it = pidx.items[i];
    if (x < it.x0 - tol || x > it.x1 + tol || z < it.z0 - tol || z > it.z1 + tol || !inRing(it.ring, x, z, tol)) continue;
    const y = planeY(it, x, z);
    if (y > by) { by = y; best = i; }
  }
  return best;
}
// the plan distance from (x, z) to the ring of item it -> { d, side }
function ringDist(it, x, z) {
  const R = it.ring, n = R.length / 2;
  let bd = Infinity, bs = -1;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n, ax = R[2 * i], az = R[2 * i + 1], dx = R[2 * j] - ax, dz = R[2 * j + 1] - az, l2 = dx * dx + dz * dz;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / l2)) : 0;
    const d = Math.hypot(x - ax - t * dx, z - az - t * dz);
    if (d < bd) { bd = d; bs = i; }
  }
  return { d: bd, side: bs };
}
// (x, z) within tol m of a TRACK-FACING side (edge true) of any item: the
// item index, else -1 (the clipper's wall: its ground side keeps the bed)
function platformEdgeAt(pidx, x, z, tol = VERTEX_TOL_M) {
  if (!pidx) return -1;
  const a = platCands(pidx, x, z);
  if (!a) return -1;
  for (const i of a) {
    const it = pidx.items[i], R = it.ring, n = R.length / 2;
    if (x < it.x0 - tol || x > it.x1 + tol || z < it.z0 - tol || z > it.z1 + tol) continue;
    for (let s = 0; s < n; s++) {
      if (!it.edge[s]) continue;
      const j = (s + 1) % n, ax = R[2 * s], az = R[2 * s + 1], dx = R[2 * j] - ax, dz = R[2 * j + 1] - az, l2 = dx * dx + dz * dz;
      const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / l2)) : 0;
      if (Math.hypot(x - ax - t * dx, z - az - t * dz) <= tol) return i;
    }
  }
  return -1;
}
// the HUD's surface label of item it (client/src/ways2.js wayLabelAt: the
// drawn surface names itself): a platform "platform · <mat>", the ground
// under a canopy its <mat>
function platformLabel(it) {
  const m = (it.mat || 'paved').replace(/_/g, ' ');
  return it.kind === 'platform' ? `platform · ${m}` : m;
}
// THE GRID UNDER THE PLATFORMS (the server's game/heightfield.js and the
// client's ClientHeightfield.height, one rule): the grid's bilinear at (x, z)
// over its 4 nodes (node(i, j) -> m, node i at (i + 0.5) cell — the
// heightfield convention) with the platforms on it: inside an item its plane
// (what the pave cut draws); outside every item, in a cell whose nodes stand
// partly inside a ring whose nearest side to (x, z) is track-facing, the
// bilinear over the OUTSIDE nodes only (the bed the wall drops to: the drawn
// ground there meets the bed, not the plane); else null (the plain
// bilinear: the caller's own).
// the bed rule's side test at (x, z) outside every ring: the nearest ring
// side over the items `held` (those holding a grid node of its cell / its
// source triangle) is track-facing (platformGround; borecut's ground beside
// a side takes the bed there too)
function platformBedSide(pidx, x, z, held) {
  let bd = Infinity, edge = 0;
  for (const q of held) {
    const it = pidx.items[q], r = ringDist(it, x, z);
    if (r.d < bd) { bd = r.d; edge = it.edge[r.side]; }
  }
  return !!edge;
}
function platformGround(pidx, x, z, cell, node) {
  if (!pidx || !platCands(pidx, x, z)) return null;
  const p = platformAt(pidx, x, z);
  if (p >= 0) return planeY(pidx.items[p], x, z);
  const fx = x / cell - 0.5, fz = z / cell - 0.5, i0 = Math.floor(fx), j0 = Math.floor(fz), dx = fx - i0, dz = fz - j0;
  const W = [(1 - dx) * (1 - dz), dx * (1 - dz), (1 - dx) * dz, dx * dz];
  let inside = 0;
  const held = [];
  for (let c = 0; c < 4; c++) {
    const q = platformAt(pidx, (i0 + (c & 1) + 0.5) * cell, (j0 + (c >> 1) + 0.5) * cell);
    if (q >= 0) { inside |= 1 << c; if (!held.includes(q)) held.push(q); }
  }
  if (!inside || inside === 15 || !platformBedSide(pidx, x, z, held)) return null;
  let sw = 0, sy = 0;
  for (let c = 0; c < 4; c++) {
    if (inside & (1 << c)) continue;
    const h = node(i0 + (c & 1), j0 + (c >> 1));
    if (!Number.isFinite(h)) continue;
    sw += W[c]; sy += W[c] * h;
  }
  if (sw > 0) return sy / sw;
  // (x, z) on the inside nodes' corner of the cell: the outside nodes'
  // plain mean
  let n = 0;
  sy = 0;
  for (let c = 0; c < 4; c++) {
    if (inside & (1 << c)) continue;
    const h = node(i0 + (c & 1), j0 + (c >> 1));
    if (Number.isFinite(h)) { sy += h; n++; }
  }
  return n ? sy / n : null;
}

// a box grid over cells (GRID_M buckets): key -> cell indices (a step
// generator: paveIndex's, see STEPS there)
function* gridOf(cells) {
  const grid = new Map();
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    for (let gz = Math.floor(c.z0 / GRID_M); gz <= Math.floor(c.z1 / GRID_M); gz++) {
      for (let gx = Math.floor(c.x0 / GRID_M); gx <= Math.floor(c.x1 / GRID_M); gx++) {
        const k = gx + ':' + gz;
        let a = grid.get(k);
        if (!a) grid.set(k, a = []);
        a.push(i);
      }
    }
    if ((i & STEP_CELLS) === STEP_CELLS) yield;
  }
  return grid;
}

// AN INTEGER-CELL MULTIMAP for the index's own scratch tables (the snapper,
// shareLines): the exact replacement of a `Map` keyed by a cell pair
// `gx + ':' + gz` (or one number: kz 0) whose value is a flat list grown by
// push — the same buckets (keys compare like the string / SameValueZero:
// NaN = NaN, -0 = 0), each in insertion order — on typed arrays: no key
// string, no heap number, no array per bucket (the Map version spent ~60 %
// of paveIndex on keys and their garbage). Open addressing over the key
// pair; a bucket is a linked list of `stride` values in one pool.
//   M.add(x, z) -> entry e (write M.v[e * stride + j] AFTER the call: the
//   pool may move); M.first(x, z) / M.last(x, z) -> the bucket's first /
//   last entry | -1, M.nx[e] -> the next (-1 = end). Scratch only: the
//   index paveIndex returns holds none of these.
const _same = (a, b) => a === b || (a !== a && b !== b);
class CellMap {
  constructor(stride, cap = 1 << 12) {
    this.s = stride;
    this._slots(cap);
    this.n = 0;
    this.v = new Float64Array(stride * cap);
    this.nx = new Int32Array(cap);
  }
  // head / tail hold entry + 1 (0 = an empty slot: a fresh table needs no fill)
  _slots(cap) {
    this.cap = cap; this.mask = cap - 1; this.used = 0;
    this.kx = new Float64Array(cap); this.kz = new Float64Array(cap);
    this.hd = new Int32Array(cap); this.tl = new Int32Array(cap);
  }
  _hash(x, z) {
    let h = Math.imul(x | 0, 0x9E3779B1) ^ Math.imul((x / 4294967296) | 0, 0x85EBCA77) ^ Math.imul(z | 0, 0xC2B2AE3D);
    h ^= h >>> 15; h = Math.imul(h, 0x2C1B3C6D); h ^= h >>> 12;
    return h & this.mask;
  }
  // the first entry of bucket (x, z), -1 when there is none; nx[e] the next
  first(x, z) {
    const kx = this.kx, kz = this.kz, hd = this.hd, m = this.mask;
    for (let i = this._hash(x, z); ; i = (i + 1) & m) {
      if (hd[i] === 0) return -1;
      if (_same(kx[i], x) && _same(kz[i], z)) return hd[i] - 1;
    }
  }
  // the last entry of bucket (x, z), -1 when there is none
  last(x, z) {
    const kx = this.kx, kz = this.kz, hd = this.hd, m = this.mask;
    for (let i = this._hash(x, z); ; i = (i + 1) & m) {
      if (hd[i] === 0) return -1;
      if (_same(kx[i], x) && _same(kz[i], z)) return this.tl[i] - 1;
    }
  }
  add(x, z) {
    if (2 * (this.used + 1) > this.cap) drain(this.grow());
    let i = this._hash(x, z);
    const kx = this.kx, kz = this.kz, hd = this.hd, m = this.mask;
    while (hd[i] !== 0 && !(_same(kx[i], x) && _same(kz[i], z))) i = (i + 1) & m;
    if (this.n === this.nx.length) {
      const nx = new Int32Array(this.n * 2); nx.set(this.nx); this.nx = nx;
      const v = new Float64Array(this.v.length * 2); v.set(this.v); this.v = v;
    }
    const e = this.n++;
    this.nx[e] = -1;
    if (hd[i] === 0) { kx[i] = x; kz[i] = z; hd[i] = e + 1; this.used++; } else this.nx[this.tl[i] - 1] = e;
    this.tl[i] = e + 1;
    return e;
  }
  // room for `more` new buckets without a rehash inside a step: the steps
  // call it at their yield points (`yield* M.reserve(k)`), so a big table's
  // rehash is spread over steps too, never one long pause
  *reserve(more) {
    while (2 * (this.used + more) > this.cap) yield* this.grow();
  }
  *grow() {
    const { kx, kz, hd, tl, cap } = this;
    this._slots(cap * 2);
    for (let j = 0; j < cap; j++) {
      if ((j & 65535) === 65535) yield;
      if (hd[j] === 0) continue;
      let i = this._hash(kx[j], kz[j]);
      while (this.hd[i] !== 0) i = (i + 1) & this.mask;
      this.kx[i] = kx[j]; this.kz[i] = kz[j]; this.hd[i] = hd[j]; this.tl[i] = tl[j]; this.used++;
    }
  }
}

// WALK EDGES ON THE ROAD'S LINES. A walk's outer edge runs along its road's
// formation edge, a corner on a section line — computed by the WORLD stage
// as its own polyline (vertices <= 1 m apart, rounded in ways.json), so
// it lies a few mm off the road cells' sides: a chord across a curved side
// misses it by the sagitta. Where the walk's edge lies inside the formation
// the band between is road (road level, 12 cm under the walk top) and the
// territory beyond it would meet road level, not the walk. So the walk takes
// the road's lines by construction:
//  1. every ring vertex takes, the first that applies: a road corner within
//     SNAP_V_M; its projection on the nearest road side within SNAP_E_M (or
//     a road corner within SNAP_V_M of that); a walk vertex already placed
//     within SNAP_V_M; its projection on an earlier walk's side within
//     SNAP_V_M; a road corner within SNAP_E_M; else it stays. (Road lines
//     first: a walk point placed near, not on, a road line never pulls a
//     later vertex off it.)
//  2. every outer (non-kerb) ring edge takes, in order, every road corner
//     within SNAP_E_M of it (strictly between its ends): the walk's outer
//     edge then runs through the road side's own vertices, and between two
//     of them along that very side.
// The ring moves by under SNAP_E_M; walksIndex's item is rebuilt in place
// (ring, ky — interpolated on inserted vertices —, kerb pairs, kseg, box):
// walkTopAt reads the snapped walk. Road cells' corners are the first
// canonical points, then every walk vertex in item order (two walks that
// meet share their vertices exactly).
const SNAP_V_M = 2e-3, SNAP_E_M = 0.02, SNAP_SEG_M = 2;
function* snapper(roads) {
  // the five tables (CellMap: the same buckets as the string-keyed Maps
  // they replace, in the same order): road corners by SNAP_V_M cell (rp)
  // and by SNAP_SEG_M cell (rc), road sides by SNAP_SEG_M cell (rs), walk
  // points (wp), walk sides (wsg)
  const rp = new CellMap(2), wp = new CellMap(2), rs = new CellMap(4), wsg = new CellMap(4), rc = new CellMap(2);
  const put2 = (M, gx, gz, a, b) => { const e = M.add(gx, gz) * 2, v = M.v; v[e] = a; v[e + 1] = b; };
  const segInto = (M, ax, az, bx, bz) => {
    for (let gz = Math.floor((Math.min(az, bz) - SNAP_E_M) / SNAP_SEG_M); gz <= Math.floor((Math.max(az, bz) + SNAP_E_M) / SNAP_SEG_M); gz++) {
      for (let gx = Math.floor((Math.min(ax, bx) - SNAP_E_M) / SNAP_SEG_M); gx <= Math.floor((Math.max(ax, bx) + SNAP_E_M) / SNAP_SEG_M); gx++) {
        const e = M.add(gx, gz) * 4, v = M.v;
        v[e] = ax; v[e + 1] = az; v[e + 2] = bx; v[e + 3] = bz;
      }
    }
  };
  for (let ci = 0; ci < roads.length; ci++) {
    const G = roads[ci].seg;
    for (let k = 0; k < G.length; k += 4) {
      put2(rp, Math.floor(G[k] / SNAP_V_M), Math.floor(G[k + 1] / SNAP_V_M), G[k], G[k + 1]);
      put2(rc, Math.floor(G[k] / SNAP_SEG_M), Math.floor(G[k + 1] / SNAP_SEG_M), G[k], G[k + 1]);
      segInto(rs, G[k], G[k + 1], G[k + 2], G[k + 3]);
    }
    if ((ci & STEP_CELLS) === STEP_CELLS) {
      yield;
      yield* rp.reserve(4 * (STEP_CELLS + 1)); yield* rc.reserve(4 * (STEP_CELLS + 1)); yield* rs.reserve(64 * (STEP_CELLS + 1));
    }
  }
  const out = [0, 0];
  // the nearest point of M within r (r <= SNAP_V_M: the 3x3 key cells)
  const nearPt = (M, x, z, r) => {
    const gx = Math.floor(x / SNAP_V_M), gz = Math.floor(z / SNAP_V_M);
    let bd = r, bx = NaN, bz = NaN;
    const v = M.v, nx = M.nx;
    for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
      for (let e = M.first(gx + dx, gz + dz); e >= 0; e = nx[e]) { const i = 2 * e, d = Math.hypot(x - v[i], z - v[i + 1]); if (d <= bd) { bd = d; bx = v[i]; bz = v[i + 1]; } }
    }
    if (bx === bx) { out[0] = bx; out[1] = bz; return true; }
    return false;
  };
  // the projection on the nearest side of M within r (strictly inside it)
  const nearSide = (M, x, z, r) => {
    let be = r, ex = NaN, ez = NaN;
    const v = M.v, nx = M.nx;
    for (let e = M.first(Math.floor(x / SNAP_SEG_M), Math.floor(z / SNAP_SEG_M)); e >= 0; e = nx[e]) {
      const i = 4 * e;
      const ax = v[i], az = v[i + 1], dx = v[i + 2] - ax, dz = v[i + 3] - az, l2 = dx * dx + dz * dz;
      const t = l2 > 0 ? ((x - ax) * dx + (z - az) * dz) / l2 : -1;
      if (!(t > 0 && t < 1)) continue;
      const qx = ax + t * dx, qz = az + t * dz, d = Math.hypot(x - qx, z - qz);
      if (d <= be) { be = d; ex = qx; ez = qz; }
    }
    if (ex === ex) { out[0] = ex; out[1] = ez; return true; }
    return false;
  };
  // the road corners within r of segment a -> b strictly between its ends
  // (more than SNAP_V_M from both): [t, x, z] ascending
  const cornersOn = (ax, az, bx, bz, r) => {
    const dx = bx - ax, dz = bz - az, l2 = dx * dx + dz * dz, L = Math.sqrt(l2), res = [];
    if (!(L > 2 * SNAP_V_M)) return res;
    const seen = new Set();
    const v = rc.v, nx = rc.nx;
    for (let gz = Math.floor((Math.min(az, bz) - r) / SNAP_SEG_M); gz <= Math.floor((Math.max(az, bz) + r) / SNAP_SEG_M); gz++) {
      for (let gx = Math.floor((Math.min(ax, bx) - r) / SNAP_SEG_M); gx <= Math.floor((Math.max(ax, bx) + r) / SNAP_SEG_M); gx++) {
        for (let e = rc.first(gx, gz); e >= 0; e = nx[e]) {
          const x = v[2 * e], z = v[2 * e + 1], t = ((x - ax) * dx + (z - az) * dz) / l2;
          if (!(t * L > SNAP_V_M && (1 - t) * L > SNAP_V_M)) continue;
          if (Math.abs((x - ax) * dz - (z - az) * dx) / L > r) continue;
          const k = x + ',' + z;
          if (seen.has(k)) continue;
          seen.add(k); res.push([t, x, z]);
        }
      }
    }
    res.sort((p, q) => p[0] - q[0]);
    return res;
  };
  // the nearest road corner within SNAP_E_M (the SNAP_SEG_M cell and its ring)
  const nearCorner = (x, z) => {
    const gx = Math.floor(x / SNAP_SEG_M), gz = Math.floor(z / SNAP_SEG_M);
    let bd = SNAP_E_M, bx = NaN, bz = NaN;
    const v = rc.v, nx = rc.nx;
    for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
      for (let e = rc.first(gx + dx, gz + dz); e >= 0; e = nx[e]) { const i = 2 * e, d = Math.hypot(x - v[i], z - v[i + 1]); if (d <= bd) { bd = d; bx = v[i]; bz = v[i + 1]; } }
    }
    if (bx === bx) { out[0] = bx; out[1] = bz; return true; }
    return false;
  };
  // -> 0 kept, else the rule that placed it (1 road corner, 2 road side,
  // 3 walk point, 4 walk side, 5 road corner within SNAP_E_M); out = the
  // point
  const snap = (x, z) => {
    let r = 0;
    if (nearPt(rp, x, z, SNAP_V_M)) r = 1;
    else if (nearSide(rs, x, z, SNAP_E_M)) { const px = out[0], pz = out[1]; if (nearPt(rp, px, pz, SNAP_V_M)) r = 1; else { out[0] = px; out[1] = pz; r = 2; } }
    else if (nearPt(wp, x, z, SNAP_V_M)) return 3;
    else if (nearSide(wsg, x, z, SNAP_V_M)) r = 4;
    else if (nearCorner(x, z)) r = 5;
    else { out[0] = x; out[1] = z; }
    put2(wp, Math.floor(out[0] / SNAP_V_M), Math.floor(out[1] / SNAP_V_M), out[0], out[1]);
    return r;
  };
  const addSeg = (ax, az, bx, bz) => segInto(wsg, ax, az, bx, bz);
  // room for a ring of n vertices (a step boundary: see CellMap reserve)
  function* reserve(n) { yield* wp.reserve(2 * n); yield* wsg.reserve(32 * n); }
  return { snap, out, addSeg, cornersOn, reserve };
}
// snap item it (walksIndex) in place -> the number of vertices moved or
// inserted
function snapRing(it, SN, st = null) {
  const R0 = it.ring, n0 = R0.length / 2;
  let moved = 0;
  const P = [];
  const onKerb = new Uint8Array(n0);
  for (const [i, j] of it.kpairs) { onKerb[i] = 1; onKerb[j] = 1; }
  for (let i = 0; i < n0; i++) {
    const x = R0[2 * i], z = R0[2 * i + 1];
    if (SN.snap(x, z) && (SN.out[0] !== x || SN.out[1] !== z)) {
      moved++; P.push(SN.out[0], SN.out[1]);
      if (st) {
        const d = Math.hypot(SN.out[0] - x, SN.out[1] - z);
        if (d > st.walk_snap_max_m) st.walk_snap_max_m = d;
        if (d > SNAP_V_M) st.walk_snap_over_2mm++;
        if (onKerb[i] && d > st.walk_snap_kerb_max_m) st.walk_snap_kerb_max_m = d;
      }
    } else P.push(x, z);
  }
  // the outer edges through the road corners beside them
  const kerbE = new Set();
  for (const [i] of it.kpairs) kerbE.add(i);
  const R = [], ky = [], map = new Int32Array(n0);
  for (let i = 0; i < n0; i++) {
    const j = (i + 1) % n0;
    map[i] = R.length / 2;
    R.push(P[2 * i], P[2 * i + 1]); ky.push(it.ky[i]);
    if (kerbE.has(i)) continue;
    for (const [t, x, z] of SN.cornersOn(P[2 * i], P[2 * i + 1], P[2 * j], P[2 * j + 1], SNAP_E_M)) {
      R.push(x, z); ky.push(it.ky[i] + t * (it.ky[j] - it.ky[i])); moved++;
      if (st) st.walk_vertices_inserted++;
    }
  }
  const n = R.length / 2;
  for (let i = 0; i < n; i++) { const j = (i + 1) % n; SN.addSeg(R[2 * i], R[2 * i + 1], R[2 * j], R[2 * j + 1]); }
  if (moved) {
    // the ring, its kerb pairs (kerb edges hold no inserted vertex), the
    // kerb polyline and the box from the snapped ring
    it.ring = R; it.ky = ky;
    it.kpairs = it.kpairs.map(([i, j]) => [map[i], map[j]]);
    const K = it.kseg;
    let s = 0;
    for (const [i, j] of it.kpairs) { K[s] = R[2 * i]; K[s + 1] = R[2 * i + 1]; K[s + 3] = R[2 * j]; K[s + 4] = R[2 * j + 1]; s += 6; }
    it.x0 = Infinity; it.x1 = -Infinity; it.z0 = Infinity; it.z1 = -Infinity;
    for (let i = 0; i < n; i++) {
      const px = R[2 * i], pz = R[2 * i + 1];
      if (px < it.x0) it.x0 = px; if (px > it.x1) it.x1 = px; if (pz < it.z0) it.z0 = pz; if (pz > it.z1) it.z1 = pz;
    }
  }
  return moved;
}

// ONE LINE, ONE ID (the clipper's line ids, client/src/borecut.js): cell
// sides that lie on one line — both ends within SHARE_TOL_M of it, the side
// at least SHARE_MIN_M long — share that line's half-space exactly
// (coefficients equal or negated) and its id `gid`. Road cells first (their
// sides are the exact formation / section / crown lines), then walk cells.
const SHARE_TOL_M = 1e-6, SHARE_MIN_M = 1e-3, SHARE_CELL_M = 1;
function* shareLines(order) {
  const L = [];                    // A, B, D per line
  // numeric key of a SHARE_CELL_M cell -> line ids (a CellMap keyed by the
  // one number, kz 0: the same buckets as the Map it replaces)
  const buckets = new CellMap(1, 1 << 16);
  let shared = 0;
  const key = (gx, gz) => (gx + 1048576) * 2097152 + (gz + 1048576);
  // a side's cells: the ones its segment's box meets (a side is short)
  for (let ci = 0; ci < order.length; ci++) {
    if ((ci & STEP_CELLS) === STEP_CELLS) { yield; yield* buckets.reserve(64 * (STEP_CELLS + 1)); }
    const c = order[ci];
    c.gid = new Int32Array(c.nk);
    for (let k = 0; k < c.nk; k++) {
      const px = c.seg[4 * k], pz = c.seg[4 * k + 1], qx = c.seg[4 * k + 2], qz = c.seg[4 * k + 3];
      const gx0 = Math.floor(Math.min(px, qx) / SHARE_CELL_M), gx1 = Math.floor(Math.max(px, qx) / SHARE_CELL_M);
      const gz0 = Math.floor(Math.min(pz, qz) / SHARE_CELL_M), gz1 = Math.floor(Math.max(pz, qz) / SHARE_CELL_M);
      let id = -1, sg = 1;
      if (Math.hypot(qx - px, qz - pz) >= SHARE_MIN_M) {
        // the cells of the two ends are enough: a line is registered in
        // every cell its own side's box meets, so a collinear side that
        // overlaps it (an end inside its span) or continues it (an end ON
        // its end: the next straight quad's formation edge) finds it
        for (let end = 0; end < 2 && id < 0; end++) {
          const ex = end ? qx : px, ez = end ? qz : pz;
          const bv = buckets.v, bn = buckets.nx;
          for (let e = buckets.first(key(Math.floor(ex / SHARE_CELL_M), Math.floor(ez / SHARE_CELL_M)), 0); e >= 0; e = bn[e]) {
            const l = bv[e], A = L[3 * l], B = L[3 * l + 1], D = L[3 * l + 2];
            if (Math.abs(A * px + B * pz + D) <= SHARE_TOL_M && Math.abs(A * qx + B * qz + D) <= SHARE_TOL_M) {
              id = l; sg = A * c.P[4 * k] + B * c.P[4 * k + 1] >= 0 ? 1 : -1;
              break;
            }
          }
        }
      }
      if (id >= 0) {
        c.P[4 * k] = sg * L[3 * id]; c.P[4 * k + 1] = sg * L[3 * id + 1]; c.P[4 * k + 3] = sg * L[3 * id + 2];
        shared++;
      } else {
        id = L.length / 3;
        L.push(c.P[4 * k], c.P[4 * k + 1], c.P[4 * k + 3]);
      }
      // the line is registered in every cell this side's box meets (a
      // shared line grows with its sides: a later side along any of them
      // finds it)
      for (let gz = gz0; gz <= gz1; gz++) for (let gx = gx0; gx <= gx1; gx++) {
        const kk = key(gx, gz), e0 = buckets.first(kk, 0);
        let has = false;
        if (e0 >= 0) {
          const bv = buckets.v, bn = buckets.nx;
          if (bv[buckets.last(kk, 0)] === id) continue;
          for (let e = e0; e >= 0 && !has; e = bn[e]) has = bv[e] === id;
        }
        if (!has) { const e = buckets.add(kk, 0); buckets.v[e] = id; }   // v AFTER add (the pool may move)
      }
      c.gid[k] = id;
    }
  }
  return { lines: L.length / 3, shared };
}

// opts.walks: a walks block ({rev, h_m, items}) used instead of doc.walks
// (fixtures); opts.platforms likewise for doc.platforms; opts.roads false:
// no road cells (a doc without walks: its platforms alone); opts.flights false:
// no flight precedence (the gates' red twin). -> { S, cells (flight lane
// cells first, then platform cells, then walk cells, then road cells: the
// clipper's priority), walks (walksIndex), pidx (platformsIndex), grid,
// stats }
//
// STEPS (client-load-unresponsive): the index is built by ONE step generator,
// `paveSteps`; `paveIndex` drains it in one go (the bake's ground tree,
// groundm/bakectx.mjs, and the gates). A step is at most STEP_PIECES road
// pieces, one walk item, or STEP_CELLS + 1 cells / roads of a later pass,
// plus a table's sliced growth (CellMap reserve). The doc and drawn must not
// change while the steps run.
function paveIndex(doc, drawn, opts = {}) { return drain(paveSteps(doc, drawn, opts)); }
function* paveSteps(doc, drawn, opts = {}) {
  const S = opts.S || (yield* stationSteps(doc));
  yield;
  const runs = doc.ways;
  const cells = [];
  const stats = { road_pieces: 0, road_cells: 0, road_quads_split: 0, road_quads_dropped: 0, walk_items: 0, walk_cells: 0, walk_bad: 0,
    walk_vertices_snapped: 0, walk_vertices_inserted: 0, walk_snap_max_m: 0, walk_snap_over_2mm: 0, walk_snap_kerb_max_m: 0, crown_split: 0, lines: 0, sides_shared: 0, flight_cells: 0,
    platform_items: 0, platform_cells: 0, platform_bad: 0 };
  // ---- roads: per paved drawn piece, per section gap the plan quad
  // (lo edge a, hi edge b), split at the crown line on a crowned run
  const pieces = opts.roads === false ? [] : drawn.pieces;
  for (let pi = 0; pi < pieces.length; pi++) {
    if (pi % STEP_PIECES === STEP_PIECES - 1) yield;
    const p = pieces[pi];
    const e = runs[p.run];
    if (!pavedRun(doc, e)) continue;
    stats.road_pieces++;
    const sec = p.sec, r = p.run, g0 = S.off[r];
    const crw = !!S.crowned[r];
    const at = (X, o) => {
      const f = X.hi > X.lo ? (o - X.lo) / (X.hi - X.lo) : 0.5;
      return [X.a[0] + f * (X.b[0] - X.a[0]), X.a[2] + f * (X.b[2] - X.a[2])];
    };
    for (let q = 0; q + 1 < sec.length; q++) {
      const A = sec[q], B = sec[q + 1];
      // the segments whose claims reach the quad (a cap section repeats
      // its station: the segment toward the next one)
      const ia = Math.min(A.i, B.i), ib = Math.max(A.i, B.i);
      const tag = { kind: KIND.road, run: r, gA: g0 + Math.max(0, ia - 1), gB: g0 + Math.min(e.x.length - 2, ib) };
      const quads = [];
      const ocA = S.oc[g0 + A.i], ocB = S.oc[g0 + B.i];
      if (crw && ocA > A.lo && ocA < A.hi && ocB > B.lo && ocB < B.hi) {
        const cA = at(A, ocA), cB = at(B, ocB);
        quads.push([A.a[0], A.a[2], B.a[0], B.a[2], cB[0], cB[1], cA[0], cA[1]]);
        quads.push([cA[0], cA[1], cB[0], cB[1], B.b[0], B.b[2], A.b[0], A.b[2]]);
        stats.crown_split++;
      } else quads.push([A.a[0], A.a[2], B.a[0], B.a[2], B.b[0], B.b[2], A.b[0], A.b[2]]);
      for (let Q of quads) {
        if (ringArea(Q) < 0) Q = [Q[6], Q[7], Q[4], Q[5], Q[2], Q[3], Q[0], Q[1]];
        if (convexCCW(Q)) {
          const c = cellOf(Q, tag);
          if (c) { cells.push(c); stats.road_cells++; } else stats.road_quads_dropped++;
          continue;
        }
        // a folded / bent quad: the two triangles of whichever diagonal
        // leaves both CCW (else the ones that are)
        let any = false;
        for (const T of [[0, 1, 2, 0, 2, 3], [1, 2, 3, 1, 3, 0]]) {
          const t1 = [Q[2 * T[0]], Q[2 * T[0] + 1], Q[2 * T[1]], Q[2 * T[1] + 1], Q[2 * T[2]], Q[2 * T[2] + 1]];
          const t2 = [Q[2 * T[3]], Q[2 * T[3] + 1], Q[2 * T[4]], Q[2 * T[4] + 1], Q[2 * T[5]], Q[2 * T[5] + 1]];
          if (ringArea(t1) > 1e-9 && ringArea(t2) > 1e-9) {
            for (const t of [t1, t2]) { const c = cellOf(t, tag); if (c) { cells.push(c); stats.road_cells++; } }
            any = true; break;
          }
        }
        if (!any) {
          for (const t of [[Q[0], Q[1], Q[2], Q[3], Q[4], Q[5]], [Q[0], Q[1], Q[4], Q[5], Q[6], Q[7]]]) {
            if (ringArea(t) > 1e-9) { const c = cellOf(t, tag); if (c) { cells.push(c); stats.road_cells++; any = true; } }
          }
        }
        if (any) stats.road_quads_split++; else stats.road_quads_dropped++;
      }
    }
  }
  const roads = cells.splice(0);
  // ---- walks (WAYSG2 P1): each ring snapped onto the road lines, its
  // convex parts the clipper's walk cells (before every road cell: a walk
  // claims first)
  yield;
  const WI = walksIndex(opts.walks !== undefined ? opts.walks : (doc.walks || null));
  const walks = WI ? WI.items : [];
  const hW = WI ? WI.h_m : 0;
  if (WI) {
    stats.walk_bad = WI.bad;
    const SN = yield* snapper(roads);
    for (let wi = 0; wi < walks.length; wi++) {
      yield;
      const it = walks[wi];
      yield* SN.reserve(it.ring.length / 2);
      stats.walk_items++;
      stats.walk_vertices_snapped += snapRing(it, SN, stats);
      for (const part of convexParts(it.ring)) {
        const c = cellOf(part, { kind: KIND.walk, walk: wi, run: it.side_of[0] != null ? it.side_of[0] : -1 });
        if (c) { cells.push(c); stats.walk_cells++; }
      }
    }
    // the walks' grid from the snapped boxes
    WI.grid = new Map();
    walks.forEach((it, i) => {
      for (let gz = Math.floor(it.z0 / GRID_M); gz <= Math.floor(it.z1 / GRID_M); gz++) {
        for (let gx = Math.floor(it.x0 / GRID_M); gx <= Math.floor(it.x1 / GRID_M); gx++) {
          const key = gx + ':' + gz;
          let a = WI.grid.get(key);
          if (!a) WI.grid.set(key, a = []);
          a.push(i);
        }
      }
    });
  }
  // ---- platforms: each ring's convex parts (after the flights, before
  // every walk and road cell: a platform claims its ring)
  const PI = platformsIndex(opts.platforms !== undefined ? opts.platforms : (doc.platforms || null));
  const plats = [];
  if (PI) {
    stats.platform_bad = PI.bad;
    PI.items.forEach((it, i) => {
      stats.platform_items++;
      for (const part of convexParts(it.ring)) {
        const c = cellOf(part, { kind: KIND.platform, plat: i, run: -1 });
        if (c) { plats.push(c); stats.platform_cells++; }
      }
    });
    yield;
  }
  // ---- flights (K's corridor.flight_owns_lane): A FLIGHT OWNS ITS LANE —
  // each segment between two of its stations, |o| <= w/2 (no end caps:
  // past its end stations each run's own claims decide) — before every walk
  // and road cell: inside it paveAt is no paved surface (the flight's ground
  // is its plane, as drawn today) and the re-mesh keeps the template there.
  // Only lanes that meet a walk / road cell (elsewhere nothing re-meshes).
  const flights = [];
  if (opts.flights !== false) {
    const near = roads.concat(cells, plats), ng = yield* gridOf(near);
    const meets = (c) => {
      for (let gz = Math.floor(c.z0 / GRID_M); gz <= Math.floor(c.z1 / GRID_M); gz++) {
        for (let gx = Math.floor(c.x0 / GRID_M); gx <= Math.floor(c.x1 / GRID_M); gx++) {
          for (const i of ng.get(gx + ':' + gz) || []) {
            const d = near[i];
            if (!(d.x1 < c.x0 || d.x0 > c.x1 || d.z1 < c.z0 || d.z0 > c.z1)) return true;
          }
        }
      }
      return false;
    };
    for (let r = 0; r < runs.length; r++) {
      if ((r & STEP_CELLS) === STEP_CELLS) yield;
      const e = runs[r];
      if (!WD.isFlightRun(doc, e)) continue;
      const h = S.rH[r], g0 = S.off[r];
      for (let i = 0; i + 1 < e.x.length; i++) {
        const g = g0 + i, ax = S.x[g], az = S.z[g], bx = S.x[g + 1], bz = S.z[g + 1];
        const L = Math.hypot(bx - ax, bz - az);
        if (!(L > 1e-6)) continue;
        const nx = -(bz - az) / L * h, nz = (bx - ax) / L * h;
        let Q = [ax + nx, az + nz, bx + nx, bz + nz, bx - nx, bz - nz, ax - nx, az - nz];
        if (ringArea(Q) < 0) Q = [Q[6], Q[7], Q[4], Q[5], Q[2], Q[3], Q[0], Q[1]];
        const c = cellOf(Q, { kind: KIND.flight, run: r, g });
        if (c && meets(c)) { flights.push(c); stats.flight_cells++; }
      }
    }
  }
  // (a platform side along a road / walk line adopts that line)
  const sh = yield* shareLines(flights.concat(roads, cells, plats));
  stats.lines = sh.lines; stats.sides_shared = sh.shared;
  cells.unshift(...flights, ...plats);
  for (const c of roads) cells.push(c);
  cells.forEach((c, i) => { c.id = i; });
  const grid = yield* gridOf(cells);
  // PAVE YIELDS WHERE THE CORRIDOR DID NOT PAVE: the conformed ground (the
  // baked grid: the client's L0 template, the server's heightfield) stands
  // more than yield_m off a run's surface where the conform left that cell
  // to something else — a footprint / structure it kept, another claim that
  // won, a cap it carried. There paveAt is no paved surface (yielded) and
  // the re-mesh keeps the template, so client and server agree (APP_BAKER
  // §5). yield_m = the server's ground slack (game/sim.js 'ground' 0.4 m)
  // less 0.1 m: judged at the grid nodes, while the server reads its
  // bilinear grid between them and the client its drawn triangle.
  // opts.ground(x, z): the conformed ground there — the grid nodes about
  // (x, z) (an array: the lattice triangle's support, borecut latticeSupport)
  // or one height — and a level yields only when it is more than yield_m
  // off EVERY one (a road edge whose outer node falls away keeps its
  // inner node: paved); without it paveAt cannot yield; opts.yield ===
  // false: never (the gate's twin)
  const yield_m = opts.yield === false ? null : YIELD_M;
  const wall_m = doc.corridor && doc.corridor.rules && Number.isFinite(doc.corridor.rules.kerb_m) ? doc.corridor.rules.kerb_m : 0.3;
  return { S, cells, walks, widx: WI, pidx: PI, h_m: hW, wall_m, yield_m, ground: opts.ground || null, grid, stats, _foot: new Float64Array(2), _stamp: new Uint32Array(cells.length), _tick: 0 };
}

// the cells whose box meets [x0, x1] x [z0, z1] (ascending id: walk cells
// first, the clipper's priority)
function cellsIn(idx, x0, x1, z0, z1) {
  const out = [];
  if (!idx.cells.length) return out;
  if (++idx._tick === 0xFFFFFFFF) { idx._stamp.fill(0); idx._tick = 1; }
  const t = idx._tick;
  for (let gz = Math.floor(z0 / GRID_M); gz <= Math.floor(z1 / GRID_M); gz++) {
    for (let gx = Math.floor(x0 / GRID_M); gx <= Math.floor(x1 / GRID_M); gx++) {
      const a = idx.grid.get(gx + ':' + gz);
      if (!a) continue;
      for (const i of a) {
        if (idx._stamp[i] === t) continue;
        idx._stamp[i] = t;
        const c = idx.cells[i];
        if (c.x1 < x0 || c.x0 > x1 || c.z1 < z0 || c.z0 > z1) continue;
        out.push(c);
      }
    }
  }
  out.sort((a, b) => a.id - b.id);
  return out;
}

// the ROAD LEVEL of run r at (x, z): the min over its segments gA..gB (a
// cell's) whose claim foot lies on the segment of the carriageway height
// there (the conform: the lowest claim wins); none -> the nearest segment's
// end section carried (t clamped)
function roadOfRun(idx, r, gA, gB, x, z) {
  const S = idx.S, F = idx._foot;
  let best = Infinity, bo = 0;
  for (let g = gA; g <= gB; g++) {
    if (!segFoot(S, g, x, z, F)) continue;
    const y = carriageway(S, g, F[0], F[1]);
    if (y < best) { best = y; bo = F[1]; }
  }
  if (best < Infinity) return { y: best, o: bo };
  // no foot (a cap past the last section): the nearer end section's plane
  let bd = Infinity, bg = gA, bt = 0;
  for (let g = gA; g <= gB + 1; g++) {
    const d = Math.hypot(x - S.x[g], z - S.z[g]);
    if (d < bd) { bd = d; bg = g; }
  }
  const o = (x - S.x[bg]) * S.nx[bg] + (z - S.z[bg]) * S.nz[bg];
  if (bg > gB) { bg = gB; bt = 1; }
  return { y: carriageway(S, bg, bt, o), o };
}
// the walk top of item w of a pave index at (x, z)
function walkTop(idx, w, x, z) { return walkTopOf(idx.walks[w], idx.h_m, x, z); }

// the road level at (x, z) over the road cells holding it (depth >= -tol),
// or null; the walk top over the walk cells holding it, or null
function roadY(idx, x, z, tol = 0, cs = null) {
  cs = cs || cellsIn(idx, x - tol, x + tol, z - tol, z + tol);
  let y = Infinity, run = -1, o = 0, seen = null;
  for (const c of cs) {
    if (c.kind !== KIND.road || cellDepth(c, x, z) < -tol) continue;
    const key = c.run * 1048576 + c.gA;
    if (seen && seen.has(key)) continue;
    (seen || (seen = new Set())).add(key);
    const v = roadOfRun(idx, c.run, c.gA, c.gB, x, z);
    if (v.y < y) { y = v.y; run = c.run; o = v.o; }
  }
  return y < Infinity ? { y, run, o } : null;
}
function walkY(idx, x, z, tol = 0, cs = null) {
  cs = cs || cellsIn(idx, x - tol, x + tol, z - tol, z + tol);
  let best = null;
  for (const c of cs) {
    if (c.kind !== KIND.walk || cellDepth(c, x, z) < -tol) continue;
    const v = walkTop(idx, c.walk, x, z);
    if (!best || v.y > best.y) best = { y: v.y, walk: c.walk, run: c.run, d: v.d };
  }
  return best;
}

// THE YIELD (paveIndex yield_m; see there): the sim's ground slack 0.4 m less
// 0.1 m for the in-cell difference (grid nodes vs the server's bilinear)
const YIELD_M = 0.3;

// a paved level y yields to the conformed ground g (one height or the grid
// nodes about the point): more than m off every finite one (none: never)
function yieldsTo(g, y, m) {
  if (typeof g === 'number') return Number.isFinite(g) && Math.abs(y - g) > m;
  let any = false;
  for (let i = 0; i < g.length; i++) {
    if (!Number.isFinite(g[i])) continue;
    if (Math.abs(y - g[i]) <= m) return false;
    any = true;
  }
  return any;
}

// THE ONE SURFACE RULE: { y, kind: 'road' | 'walk' | 'platform' | null,
// run, edge [, flight] [, plat] } (edge: a walk's distance to its kerb line,
// a road's to its drawn half width, a platform's to its ring, m).
// Precedence: a flight's lane (y null, flight = its run: its ground is its
// plane) > a platform (its plane; plat = its item; never yields: the grid
// stands on the plane, client/src/heightfield.js and the server's read it)
// > a walk > a road (min over its pieces); a walk / road more than yield_m
// off the conformed ground (idx.ground) yields (y null, yielded true: the
// corridor did not pave it).
function paveAt(idx, x, z) {
  const cs = cellsIn(idx, x, x, z, z);
  if (!cs.length) return { y: null, kind: null, run: -1, edge: null };
  // a flight's lane wins over walk and road: no paved surface (the flight's
  // own ground), flight = its run
  for (const c of cs) if (c.kind === KIND.flight && cellDepth(c, x, z) >= 0) return { y: null, kind: null, run: -1, edge: null, flight: c.run };
  for (const c of cs) {
    if (c.kind !== KIND.platform || cellDepth(c, x, z) < 0) continue;
    const it = idx.pidx.items[c.plat];
    return { y: planeY(it, x, z), kind: 'platform', run: -1, edge: ringDist(it, x, z).d, plat: c.plat };
  }
  let w = walkY(idx, x, z, 0, cs), r = roadY(idx, x, z, 0, cs), yielded = false;
  if ((w || r) && idx.yield_m != null && idx.ground) {
    const g = idx.ground(x, z);
    if (w && yieldsTo(g, w.y, idx.yield_m)) { w = null; yielded = true; }
    if (r && yieldsTo(g, r.y, idx.yield_m)) { r = null; yielded = true; }
  }
  if (w) return { y: w.y, kind: 'walk', run: w.run, edge: w.d };
  if (r) return { y: r.y, kind: 'road', run: r.run, edge: idx.S.rH[r.run] - Math.abs(r.o) };
  return yielded ? { y: null, kind: null, run: -1, edge: null, yielded: true } : { y: null, kind: null, run: -1, edge: null };
}

// A WALK ACROSS A LANE WALL (K's corridor 'walled'): lane levels under or
// beside a walk further apart than the corridor's kerb_m (doc.corridor.rules,
// idx.wall_m), e.g. a hairpin walk spanning both legs. The corridor keeps
// both levels there, with a wall between them. The walk top follows each
// kerb's own level and jumps on the nearest-kerb bisector, and the other
// leg's road meets it off by the legs' gap; one lattice triangle cannot
// follow either. Returns the span (m) of the lane levels within reach m of
// (x, z) (the kerb levels of the walks near it, the road pieces' levels),
// when a walk is near and the span exceeds wall_m; else 0.
const WALL_REACH_M = 0.75;
function walledAt(idx, x, z, reach = WALL_REACH_M) {
  if (!idx.widx) return 0;
  const items = new Set();
  for (const c of cellsIn(idx, x - reach, x + reach, z - reach, z + reach)) {
    if (c.kind === KIND.walk && cellDepth(c, x, z) >= -reach) items.add(c.walk);
  }
  if (!items.size) return 0;
  // the lane levels: each walk's kerb level (top - h_m) about (x, z), and
  // each road piece's level within reach
  let lo = Infinity, hi = -Infinity;
  for (const w of items) {
    const it = idx.walks[w];
    for (const f of [0, 0.5, 1]) {
      for (let a = 0; a < 8; a++) {
        if (f === 0 && a) break;
        const y = walkTopOf(it, 0, x + f * reach * Math.cos(a * Math.PI / 4), z + f * reach * Math.sin(a * Math.PI / 4)).y;
        if (y < lo) lo = y;
        if (y > hi) hi = y;
      }
    }
  }
  for (const c of cellsIn(idx, x - reach, x + reach, z - reach, z + reach)) {
    if (c.kind !== KIND.road || cellDepth(c, x, z) < -reach) continue;
    const y = roadOfRun(idx, c.run, c.gA, c.gB, x, z).y;
    if (y < lo) lo = y;
    if (y > hi) hi = y;
  }
  return hi - lo > idx.wall_m ? hi - lo : 0;
}

// THE CLIPPER'S HEIGHT CALLBACK (client/src/borecut.js cutTile pave.at):
// a re-meshed vertex of kind 1 (road) / 2 (walk) / 4 (platform) at (x, z)
// -> y | NaN, its cell membership within tol m (a vertex on a cell line is
// in float noise)
function heightOf(idx, kind, x, z, tol = VERTEX_TOL_M) {
  if (kind === KIND.road) { const r = roadY(idx, x, z, tol); return r ? r.y : NaN; }
  if (kind === KIND.platform) { const p = platformAt(idx.pidx, x, z, tol); return p < 0 ? NaN : planeY(idx.pidx.items[p], x, z); }
  const y = walkTopAt(idx.widx, x, z, tol);
  return y === null ? NaN : y;
}
// the clipper's pave argument for the tile box [x0, x1] x [z0, z1] (world),
// or null when no paved cell meets it (the tile keeps its template):
// at(label, x, z) the height (the clipper's labels: 1 road, 2 walk, 3
// platform — borecut PAVE_*), plat(x, z) inside a platforms ring (strict: a
// grid node the WORLD stage set on its plane; platAt the item, -1 none),
// edge(x, z) on a track-facing platform side (within 1 mm: the wall's foot
// keeps the bed), bedSide(x, z, held) platformBedSide
function tilePave(idx, x0, x1, z0, z1) {
  const cells = cellsIn(idx, x0, x1, z0, z1);
  const P = idx.pidx;
  return cells.length ? { cells, at: (k, x, z) => heightOf(idx, k === 3 ? KIND.platform : k, x, z), yieldM: idx.yield_m,
    plat: (x, z) => platformAt(P, x, z) >= 0, platAt: (x, z) => platformAt(P, x, z), edge: (x, z) => platformEdgeAt(P, x, z) >= 0,
    bedSide: (x, z, held) => platformBedSide(P, x, z, held) } : null;
}

module.exports = {
  VERTEX_TOL_M, heightOf, tilePave,
  // the section (world/corridor.js calls these)
  slopeOf, sectionParamsOf, sectionParamsDoc, crowned, sectionTables, secDz, secDzI, crownDzI, prismDz, laneSurface,
  segFoot, laneHi, carriageway, stationsOf, applyRamps,
  // the walks (P1: the walk top rule)
  walksIndex, walkTopAt, walkTopOf, inRing,
  // the platforms (flat paved areas: railway platforms, ground under canopies)
  PLATFORMS_REV, platformsIndex, planeY, platformAt, platformEdgeAt, ringDist, platformBedSide, platformGround, platformLabel,
  // the index
  KIND, GRID_M, pavedRun, ringArea, convexParts, convexCCW, cellOf, cellDepth, paveIndex, paveSteps, cellsIn, roadOfRun, walkTop, roadY, walkY, paveAt,
  WALL_REACH_M, walledAt, YIELD_M, yieldsTo,
};
